import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import React, { useCallback, useMemo } from "react";
import {
  ComponentMultiStyleConfig,
  Flex,
  Link,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { DeleteIconButton } from "../../icon-button";
import { CMDatetimeText } from "../../text";
import { hasValue } from "../../../lib/util/common-util";
import { FileUploadItemContent } from "../types";

export type CMFileUploadItemProps = {
  editMode?: "editable" | "disabled" | "readOnly";

  /**
   * アップロード済みファイルの1レコード分の情報
   */
  fileItem: FileUploadItemContent;

  /**
   * 変更モードで新規追加されたファイルに表示するメッセージ
   */
  messageAddition?: string;

  /**
   * 変更モードで削除されたファイルに表示するメッセージ
   */
  messageDelete?: string;

  /**
   * ファイル選択時のダウンロード要求通知
   * @param fileId ファイルID
   * @param fileName ファイル名
   */
  onDownload?: (fileId: string, fileName: string) => void;

  // /**
  //  * ファイル削除の確認要求
  //  * @param fileId ファイルID
  //  */
  // onConfirmDelete?: (fileId: string) => Promise<boolean>;

  /**
   * 削除結果を呼び出し元に通知
   */
  onDelete?: () => void;
} & ComponentStyleProps;

export const CMFileUploadItem = ({
  editMode = "editable",

  fileItem,

  messageAddition,
  messageDelete,

  onDownload,
  // onConfirmDelete,
  onDelete,

  sx,
  ...rest
}: CMFileUploadItemProps) => {
  const styles = useMultiStyleConfig("CMFileUploadItemStyles");

  const disabled = editMode === "disabled" || editMode === "readOnly";

  const diffModeVariant: "none" | "added" | "deleted" = useMemo(() => {
    return fileItem?.diffModeVariant ?? "none";
  }, [fileItem?.diffModeVariant]);

  /**
   * 変更モードで表示するラベルの設定
   */
  const diffStateLabel = useMemo<string | null>(() => {
    switch (diffModeVariant) {
      case "added":
        return messageAddition ?? null;
      case "deleted":
        return messageDelete ?? null;
      default:
        return null;
    }
  }, [diffModeVariant, messageAddition, messageDelete]);

  const appliedFileStyles = useMemo(
    () => ({
      ...styles.fileBase,
      ...(diffModeVariant === "added" ? styles.fileAddition : undefined),
      ...(diffModeVariant === "deleted" ? styles.fileDeleted : undefined),
    }),
    [styles, diffModeVariant],
  );

  /**
   * 削除ボタン押下時の実行確認処理
   */
  const handleConfirmDelete = async () => {
    onDelete?.();
    // // 呼び出し元に確認しない場合、削除処理を実行
    // if (isNullish(onConfirmDelete)) {
    //   // 削除処理を実行
    //   onDelete?.();
    // } else {
    //   // 呼び出し元に削除確認を行い、削除処理を実行
    //   const confirmResult = await onConfirmDelete(fileItem.id);
    //
    //   if (confirmResult) {
    //     // 呼び出し元に削除確認を行い、trueの場合は実行
    //     onDelete?.();
    //   }
    // }
  };

  const handleDownload = useCallback(() => {
    // 呼び出し元に通知
    onDownload?.(fileItem.id, fileItem.name);
  }, [onDownload, fileItem]);

  return (
    <Flex direction={"row"} sx={{ ...styles.container, ...sx }} {...rest}>
      <Link sx={appliedFileStyles} onClick={handleDownload}>
        <Text sx={styles.fileNameText}>{fileItem.name}</Text>
      </Link>
      <CMDatetimeText value={fileItem.date} sx={styles.date} />

      {!disabled && diffModeVariant !== "deleted" && (
        <DeleteIconButton
          tipMessage={fileItem.deleteButtonTip}
          ariaLabel="delete-button"
          onClick={handleConfirmDelete}
          sx={styles.deleteButton}
        />
      )}
      {hasValue(diffStateLabel) && (
        <Text sx={styles.stateLabel}>{diffStateLabel}</Text>
      )}
    </Flex>
  );
};

export const CMFileUploadItemStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "fileBase",
    "fileNameText",
    "fileAddition",
    "fileDeleted",
    "date",
    "deleteButton",
    "stateLabel",
  ],
  baseStyle: {
    container: {
      alignItems: "baseline",
    },
    fileBase: {
      overflow: "hidden",
      px: "3px",
      // maxW: "470px",
    },
    fileNameText: {
      fontSize: "sm",
      color: "blue.600",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    fileAddition: {
      borderWidth: "1px",
      borderColor: "orange.500",
    },
    fileDeleted: {
      color: "gray.400",
      backgroundColor: "gray.600",
    },
    date: {
      ml: "4px",
      fontSize: "xs",
      color: "gray.500",
    },
    deleteButton: {
      ml: "4px",
      borderRadius: "20px",
      h: "20px",
      w: "20px",
      minW: "20px",
      display: "flex",
      alignItems: "center",

      fontSize: "12px",
      color: "gray.400",

      _hover: {
        bgColor: "gray.100",
        color: "gray.600",
      },
    },

    stateLabel: {
      fontSize: "sm",
      color: "gray.900",
    },
  },
};
